import { Briefcase, Landmark, MapPin, Users, type LucideIcon } from 'lucide-react'

import {
  EntityMetadataInlineRow,
  EntityMetadataRow,
  entityMetadataRowClass,
} from '@/components/entity-metadata-row'
import {
  formatCompanySizeLabel,
  formatFoundedYearLabel,
  type OrganisationFactKind,
  type OrganisationFactsInput,
} from '@/lib/organisation-company-meta'
import { formatOrganisationLocationsSummary } from '@/lib/organisation-locations'
import { formatEntityLocation } from '@/utils/format-entity-location'
import { cn } from '@/lib/utils'

const FACT_ICONS: Record<Exclude<OrganisationFactKind, 'location'>, LucideIcon> = {
  company_size: Users,
  founded_year: Landmark,
}

function FactChip({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <span className={cn(entityMetadataRowClass, 'inline-flex w-auto')}>
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 break-words">{label}</span>
    </span>
  )
}

function OrganisationFactChip({
  kind,
  label,
}: {
  kind: Exclude<OrganisationFactKind, 'location'>
  label: string
}) {
  return <FactChip icon={FACT_ICONS[kind]} label={label} />
}

export type EntityOrganisationCompanyFactsRowProps = OrganisationFactsInput & {
  className?: string
}

/** Company size + founded year chips. */
export function EntityOrganisationCompanyFactsRow({
  company_size,
  founded_year,
  className,
}: EntityOrganisationCompanyFactsRowProps) {
  const sizeLabel = formatCompanySizeLabel(company_size)
  const foundedLabel = formatFoundedYearLabel(founded_year ?? null)
  if (!sizeLabel && !foundedLabel) return null

  return (
    <EntityMetadataInlineRow className={className}>
      {sizeLabel ? <OrganisationFactChip kind="company_size" label={sizeLabel} /> : null}
      {foundedLabel ? <OrganisationFactChip kind="founded_year" label={foundedLabel} /> : null}
    </EntityMetadataInlineRow>
  )
}

export type EntityOrganisationFactsBlockProps = OrganisationFactsInput & {
  industry?: string | null
  className?: string
  /** Public company page: industry and location share one wrapping row. */
  industryLocationLayout?: 'stacked' | 'inline'
  /** When false, size and founded are omitted so the caller can place them elsewhere. */
  showCompanyFacts?: boolean
}

/** Industry, location row, then company size + founded for org identity read surfaces. */
export function EntityOrganisationFactsBlock({
  industry,
  company_size,
  founded_year,
  city,
  country,
  locations,
  className,
  industryLocationLayout = 'stacked',
  showCompanyFacts = true,
}: EntityOrganisationFactsBlockProps) {
  const industryLine = industry?.trim() ?? ''
  const locationLine =
    formatOrganisationLocationsSummary(locations) || formatEntityLocation(city, country)
  const hasCompanyRow =
    showCompanyFacts &&
    Boolean(formatCompanySizeLabel(company_size) || formatFoundedYearLabel(founded_year ?? null))
  const inlineIdentity = industryLocationLayout === 'inline'

  if (!industryLine && !locationLine && !hasCompanyRow) {
    return null
  }

  const identityRow =
    inlineIdentity && (industryLine || locationLine) ? (
      <EntityMetadataInlineRow>
        {industryLine ? <FactChip icon={Briefcase} label={industryLine} /> : null}
        {locationLine ? <FactChip icon={MapPin} label={locationLine} /> : null}
      </EntityMetadataInlineRow>
    ) : (
      <>
        {industryLine ? (
          <EntityMetadataRow icon={Briefcase}>
            <span>{industryLine}</span>
          </EntityMetadataRow>
        ) : null}
        {locationLine ? (
          <EntityMetadataRow icon={MapPin}>
            <span>{locationLine}</span>
          </EntityMetadataRow>
        ) : null}
      </>
    )

  return (
    <div className={cn('space-y-2', className)}>
      {identityRow}
      {hasCompanyRow ? (
        <EntityOrganisationCompanyFactsRow
          company_size={company_size}
          founded_year={founded_year}
        />
      ) : null}
    </div>
  )
}
